<script setup lang="ts">
  import { computed, type Component } from 'vue';
  import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui';
  import RulesSelectionMenu from '../../build/RulesSelectionMenu.vue';
  import type { TableCell } from '../../../model/form.types';
  import type { PermissionsOutline } from '../../../model/form-permissions-selection.store';
  import { isRulesSelectableType } from '../../../lib/rules-field-eligibility';
  import {
    isSelectionInteractionMode,
    type FormBuilderInteractionMode,
  } from '../../../lib/interaction-mode';
  import TextInputElement from '../elements/TextInputElement.vue';
  import ParagraphElement from '../elements/ParagraphElement.vue';
  import NumberInputElement from '../elements/NumberInputElement.vue';
  import DateInputElement from '../elements/DateInputElement.vue';
  import UrlInputElement from '../elements/UrlInputElement.vue';
  import EmailInputElement from '../elements/EmailInputElement.vue';
  import DropdownElement from '../elements/DropdownElement.vue';
  import FixedTextElement from '../elements/FixedTextElement.vue';
  import HeaderElement from '../elements/HeaderElement.vue';
  import ImageElement from '../elements/ImageElement.vue';
  import RadioGroupElement from '../elements/RadioGroupElement.vue';
  import CheckboxElement from '../elements/CheckboxElement.vue';
  import { RegisterNumberInputElement, IndexerTitleElement, FillTitleElement } from '../elements';
  import {
    useTableCellStyles,
    type CellBorderPattern,
  } from '../../../composables/useTableCellStyles';
  import {
    useTableCellInteractions,
    type TableCellInteractionEmit,
  } from '../../../composables/useTableCellInteractions';

  interface Props {
    cell: TableCell;
    isSelected?: boolean;
    isEditable?: boolean;
    allowScopeHover?: boolean;
    interactionMode?: FormBuilderInteractionMode;
    showRulesMenu?: boolean;
    showAddRule?: boolean;
    isDraftRuleTarget?: boolean;
    hasAppliedRule?: boolean;
    permissionOutline?: PermissionsOutline | null;
    showSelectionFill?: boolean;
    gapRowsBefore?: number;
    isChunkTop?: boolean;
    isChunkBottom?: boolean;
    totalRows: number;
    totalColumns: number;
    borderWidth?: number;
    borderColor?: string;
    borderStyle?: 'solid' | 'dashed' | 'dotted';
    borderPattern?: CellBorderPattern;
  }

  const props = withDefaults(defineProps<Props>(), {
    isSelected: false,
    isEditable: false,
    allowScopeHover: false,
    interactionMode: 'builder',
    showRulesMenu: false,
    showAddRule: false,
    isDraftRuleTarget: false,
    hasAppliedRule: false,
    permissionOutline: null,
    showSelectionFill: false,
    gapRowsBefore: 0,
    isChunkTop: false,
    isChunkBottom: false,
  });

  const emit = defineEmits<TableCellInteractionEmit>();

  const elementComponent = computed((): Component | null => {
    if (!props.cell.content) return null;

    const componentMap: Record<string, Component> = {
      TEXT_INPUT: TextInputElement,
      PARAGRAPH: ParagraphElement,
      NUMBER_INPUT: NumberInputElement,
      DATE_INPUT: DateInputElement,
      URL_INPUT: UrlInputElement,
      EMAIL_INPUT: EmailInputElement,
      DROPDOWN: DropdownElement,
      FIXED_TEXT: FixedTextElement,
      HEADER: HeaderElement,
      IMAGE: ImageElement,
      RADIO_GROUP: RadioGroupElement,
      CHECKBOX: CheckboxElement,
      REGISTER_NUMBER_INPUT: RegisterNumberInputElement,
      INDEXER_TITLE: IndexerTitleElement,
      FILL_TITLE: FillTitleElement,
    };

    return componentMap[props.cell.content.type] ?? null;
  });

  const { cellStyles, borderOverlayStyles, ruleHighlightStyles, showAppliedRuleHighlight } =
    useTableCellStyles(props);

  const {
    cellRef,
    ariaLabel,
    handleClick,
    handleMouseDown,
    handleMouseEnter,
    handleMouseMove,
    handleKeyDown,
    handleContentClick,
    handleRulesOpenChange,
  } = useTableCellInteractions(props, emit);

  const shouldHideCell = computed(() => props.cell.isMerged === true);
  const isMergedRoot = computed(() => !!props.cell.mergeGroupId && !props.cell.isMerged);
  const isSelectionMode = computed(() => isSelectionInteractionMode(props.interactionMode));
  const isRulesEmptyCell = computed(() => isSelectionMode.value && !props.cell.content);
  const isRulesNonSelectableCell = computed(
    () =>
      isSelectionMode.value &&
      (!props.cell.content || !isRulesSelectableType(props.cell.content.type))
  );

  const showRulesCellMenu = computed(
    () =>
      props.interactionMode === 'rules' &&
      !!props.showRulesMenu &&
      !isRulesNonSelectableCell.value &&
      !!elementComponent.value
  );

  const contentAlignmentClass = computed(() => {
    switch (props.cell.properties?.alignment ?? 'left') {
      case 'center':
        return 'justify-center';
      case 'right':
        return 'justify-end';
      default:
        return 'justify-start';
    }
  });

  const cellContentClasses = computed(() => [
    'cell-content flex h-full items-start w-full min-w-0 max-w-full p-px relative z-[1] overflow-hidden box-border text-[initial]',
    contentAlignmentClass.value,
  ]);

  const cellClasses = computed(() => ({
    // Marker classes (queried by row/height/resize composables) + Tailwind utilities.
    'table-cell': true,
    selected: props.isSelected,
    editable: props.isEditable,
    'has-content': !!props.cell.content,
    'has-applied-rule': showAppliedRuleHighlight.value,
    'rules-empty': isRulesEmptyCell.value,
    hidden: shouldHideCell.value,
    'merged-root': isMergedRoot.value,
    'z-[1]': props.isSelected,
    'transition-none': showAppliedRuleHighlight.value,
    'transition-[box-shadow] duration-200': !showAppliedRuleHighlight.value,
    'cursor-not-allowed': isRulesNonSelectableCell.value,
    'cursor-pointer': !isRulesNonSelectableCell.value,
  }));
</script>

<template>
  <div
    v-if="!shouldHideCell"
    ref="cellRef"
    class="flex items-center justify-center relative bg-white-01 outline-none"
    :class="cellClasses"
    :style="cellStyles"
    :data-row-index="cell.rowIndex"
    :data-col-index="cell.colIndex"
    role="gridcell"
    :aria-label="ariaLabel"
    :aria-selected="isSelected"
    :aria-colspan="cell.colSpan > 1 ? cell.colSpan : undefined"
    :aria-rowspan="cell.rowSpan > 1 ? cell.rowSpan : undefined"
    :tabindex="isEditable || (isSelectionMode && !isRulesNonSelectableCell) ? 0 : -1"
    @click="handleClick"
    @mousedown="handleMouseDown"
    @mouseenter="handleMouseEnter"
    @mousemove="handleMouseMove"
    @keydown="handleKeyDown"
  >
    <div
      v-if="borderOverlayStyles"
      class="border-overlay absolute pointer-events-none z-[2]"
      :style="borderOverlayStyles"
    />
    <div
      v-if="ruleHighlightStyles"
      class="rule-highlight-overlay absolute pointer-events-none z-[2]"
      :style="ruleHighlightStyles"
    />
    <Popover
      v-if="showRulesCellMenu"
      :open="showRulesMenu"
      @update:open="handleRulesOpenChange"
    >
      <div :class="[cellContentClasses, 'pointer-events-none']">
        <component :is="elementComponent" :element="cell.content" />
      </div>
      <PopoverTrigger as-child>
        <button
          type="button"
          class="rules-cell-click-layer absolute inset-0 z-[3] cursor-pointer border-0 bg-transparent p-0 pointer-events-auto"
          aria-label="Selecionar campo da tabela"
          @click="handleContentClick"
        />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        :side-offset="8"
        disable-portal
        disable-animations
        position-strategy="absolute"
        class="w-auto p-0 z-40"
      >
        <RulesSelectionMenu :show-add-rule="showAddRule" />
      </PopoverContent>
    </Popover>
    <div
      v-else-if="cell.content && elementComponent"
      :class="[
        cellContentClasses,
        'pointer-events-auto',
        isRulesNonSelectableCell ? 'cursor-not-allowed' : 'cursor-pointer',
      ]"
      @click="handleContentClick"
    >
      <component :is="elementComponent" :element="cell.content" />
    </div>
  </div>
</template>
